import { useApolloClient } from '@apollo/client';
import { COST_CODES_QUERY } from 'apollo/queries/cost-code-queries';
import { EQUIPMENT_QUERY } from 'apollo/queries/equipment-queries';
import { projectsAncestorsChildrenLinkQuery } from 'containers/manage-projects/projects-queries';
import _ from 'lodash';
import ICostCode from 'types/CostCode';
import ScheduleReminderType from 'types/enum/ScheduleReminderType';
import IEquipment from 'types/Equipment';
import IProject from 'types/Project';
import { IScheduleReminder } from 'types/ScheduleReminder';
import { ErrorWithMessage, toErrorWithMessage } from 'utils/errorUtils';
import { getFullNameFromMember } from 'utils/stringUtils';
import { IScheduleReminderDetails } from '../types/types';

function useSchedulingReminderPopulate() {
  const apollo = useApolloClient();

  async function populate(
    reminders: IScheduleReminder[],
    onError: (e: ErrorWithMessage) => void
  ): Promise<IScheduleReminderDetails[]> {
    const withPayloads = reminders.map(async (reminder) => {
      const payload = reminder.payload ? JSON.parse(reminder.payload) : undefined;
      const withPayload: IScheduleReminderDetails = { ...reminder };
      if (reminder.payload) {
        try {
          if (payload.project) {
            const response = await apollo.query<{ projects: IProject[] }>({
              query: projectsAncestorsChildrenLinkQuery,
              variables: { filter: { id: { equal: payload.project } } },
            });

            if (response.data.projects?.length) {
              withPayload.project = response.data.projects[0];
            } else {
              withPayload.project = null;
            }
          } else {
            withPayload.project = null;
          }
        } catch (e) {
          withPayload.project = null;
        }

        try {
          if (payload.cost_code) {
            const response = await apollo.query<{ costCodes: ICostCode[] }>({
              query: COST_CODES_QUERY,
              variables: { filter: { id: { equal: payload.cost_code } } },
            });

            if (response.data.costCodes?.length) {
              withPayload.costCode = response.data.costCodes[0];
            } else {
              withPayload.costCode = null;
            }
          }
        } catch (e) {
          withPayload.costCode = null;
        }

        try {
          if (payload?.equipment) {
            const response = await apollo.query<{ equipment: IEquipment[] }>({
              query: EQUIPMENT_QUERY,
              variables: { filter: { id: { equal: payload.equipment } } },
            });

            if (response.data.equipment.length) {
              withPayload.equipment = response.data.equipment[0];
            } else {
              withPayload.equipment = null;
            }
          }
        } catch (e) {
          withPayload.equipment = null;
        }

        if (payload?.instructions) {
          withPayload.instructions = payload.instructions;
        }

        if (payload?.color && reminder.type !== ScheduleReminderType.BREAK) {
          withPayload.color = payload.color;
        }

        withPayload.links = _.sortBy(reminder.links, (link) => getFullNameFromMember(link.member));
        return withPayload;
      } else {
        return reminder;
      }
    });

    try {
      const result = await Promise.all(withPayloads);
      return _.sortBy(result);
    } catch (e: unknown) {
      onError(toErrorWithMessage(e));
      return [];
    }
  }

  return populate;
}

export default useSchedulingReminderPopulate;
